import { useState } from "react";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImportReview } from "@/components/ImportReview";
import { ImportTemplate } from "@/components/ImportTemplate";
import { SERVICES } from "@/lib/constants";

const FIELDS = [["full_name", "Contact name *"], ["phone", "Phone *"], ["email", "Email"], ["company_name", "Company"], ["service_category", "Service"], ["estimated_budget", "Budget"], ["source", "Source"]];
const sel = "h-10 border-neutral-200 bg-white text-neutral-800";

export default function Import() {
  const [step, setStep] = useState(1);
  const [up, setUp] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defService, setDefService] = useState("CUSTOM_SOFTWARE");
  const [analysis, setAnalysis] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/import/upload", fd); setUp(data); setMapping(data.suggested_mapping); setStep(2); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const analyze = async () => {
    setBusy(true);
    try { const { data } = await api.post("/import/analyze", { session_id: up.session_id, mapping, default_service: defService }); setAnalysis(data); setStep(3); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const reset = () => { setStep(1); setUp(null); setAnalysis(null); setResult(null); };

  return (
    <div className="space-y-6">
      <div><h1 className="font-display text-2xl font-bold text-[#151515]">Bulk import</h1><p className="text-sm text-neutral-500">CSV / XLSX → schema mapping → E.164 normalization → deduplication → assignment.</p></div>
      <div className="flex gap-2 font-mono text-xs">{["Upload", "Map columns", "Review & resolve", "Done"].map((s, i) => <span key={s} data-testid={`import-step-${i + 1}`} className={`rounded-md border px-2.5 py-1 ${step === i + 1 ? "border-[#1F9E7A] text-[#1F9E7A]" : step > i + 1 ? "border-emerald-300 text-emerald-700" : "border-neutral-200 text-neutral-400"}`}>{i + 1}. {s}</span>)}</div>
      {step === 1 && <ImportTemplate />}
      {step === 1 && (
        <label data-testid="import-dropzone" className="flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-neutral-200 bg-white p-12 text-center hover:border-[#1F9E7A]">
          <UploadCloud className="h-10 w-10 text-[#1F9E7A]" /><p className="font-medium text-neutral-800">{busy ? "Parsing…" : "Drop or choose a .csv / .xlsx file"}</p>
          <p className="text-xs text-neutral-400">Headers like "Phone", "Mobile", "Contact Number" are auto-mapped.</p>
          <input data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => upload(e.target.files[0])} />
        </label>
      )}
      {step === 2 && up && (
        <div className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-5">
          <p className="flex items-center gap-2 text-sm text-neutral-600"><FileSpreadsheet className="h-4 w-4 text-emerald-700" />{up.filename} · <span className="font-mono">{up.row_count}</span> rows</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {FIELDS.map(([k, t]) => (
              <div key={k}><p className="mb-1 text-xs text-neutral-500">{t}</p>
                <Select value={mapping[k] || "__none"} onValueChange={(v) => setMapping((m) => ({ ...m, [k]: v === "__none" ? undefined : v }))}>
                  <SelectTrigger data-testid={`map-field-${k}`} className={sel}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__none">— not mapped —</SelectItem>{up.headers.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select></div>
            ))}
            <div><p className="mb-1 text-xs text-neutral-500">Default service (when unmapped/unknown)</p>
              <Select value={defService} onValueChange={setDefService}><SelectTrigger data-testid="map-default-service" className={sel}><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(SERVICES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-neutral-200"><table className="w-full text-xs"><thead><tr>{up.headers.map((h) => <th key={h} className="whitespace-nowrap px-3 py-2 text-left font-mono text-neutral-400">{h}</th>)}</tr></thead>
            <tbody>{up.sample.map((r, i) => <tr key={i} className="border-t border-neutral-200">{up.headers.map((h) => <td key={h} className="whitespace-nowrap px-3 py-2 text-neutral-600">{r[h]}</td>)}</tr>)}</tbody></table></div>
          <div className="flex gap-2"><Button variant="ghost" onClick={reset} className="text-neutral-500">Back</Button>
            <Button data-testid="import-analyze-btn" disabled={busy || !mapping.full_name || !mapping.phone} onClick={analyze} className="bg-[#1F9E7A] hover:bg-[#178566]">Validate & detect duplicates</Button></div>
        </div>
      )}
      {step === 3 && analysis && <ImportReview sessionId={up.session_id} analysis={analysis} onDone={(r) => { setResult(r); setStep(4); }} />}
      {step === 4 && result && (
        <div data-testid="import-result" className="rounded-2xl border border-emerald-300 bg-emerald-50 p-6">
          <p className="flex items-center gap-2 font-display text-lg font-semibold text-emerald-700"><CheckCircle2 className="h-5 w-5" />Import complete</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">{Object.entries(result).map(([k, v]) => <div key={k} className="rounded-lg border border-neutral-200 p-3"><p className="font-mono text-[10px] uppercase text-neutral-400">{k}</p><p data-testid={`import-result-${k}`} className="font-mono text-xl text-[#1A1A1A]">{v}</p></div>)}</div>
          <Button data-testid="import-again-btn" onClick={reset} className="mt-5 bg-[#1F9E7A]">Import another file</Button>
        </div>
      )}
    </div>
  );
}
